import { autobind } from "@typed-decorators/autobind";
import { Component } from "react";
import { FormattedMessage } from "react-intl";
import onClickOutside from "react-onclickoutside";

import * as Routes from "../../generated/routes";
import { logout } from "../../logout";
import { FeatureLabel } from "../FeatureLabel";
import { Link } from "../Link";
import { OnlySmartphone } from "../shared/ResponsiveContainer";
import { UserAvatar } from "../shared/UserAvatar";

import { AssistantItemStyle, AssistantFeatureLabelStyle } from "./AvatarButton.styles";
import { TeamSwitchMenu, type Team } from "./TeamSwitchMenu";

interface Props {
  currentUser: {
    account: string;
    avatar1x: { url };
    avatar2x: { url };
    path: string;
    realName: string;
    canManageMembers: boolean;
    canPostNote?: boolean;
    canCreateLike?: boolean;
    assistantEnabled: boolean;
  };
  teams: ReadonlyArray<Team>;
  intl: any;
}

interface State {
  isMenuOpen: boolean;
}

class AvatarButtonBase extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      isMenuOpen: false,
    };
  }

  @autobind
  handleClick(event) {
    event.preventDefault();
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  }

  @autobind
  handleClickOutside() {
    if (this.state.isMenuOpen) {
      this.setState({ isMenuOpen: false });
    }
  }

  dropdownMenu() {
    const { currentUser } = this.props;

    return (
      <div className="headerNavigation-avatarDropdown dropdownBox">
        <div className="headerNavigation-avatarDropdown__menuItems">
          <ul>
            <li className="headerNavigation-avatarDropdown-currentUser">
              <Link to={currentUser.path}>
                <div className="headerNavigation-avatarDropdown-currentUser--realName">
                  {currentUser.realName || <FormattedMessage id="profile.nouns.realNameEmpty" />}
                </div>
                <div className="headerNavigation-avatarDropdown-currentUser--userName">{`@${currentUser.account}`}</div>
              </Link>
            </li>
            {currentUser.canPostNote !== false && (
              <li>
                <Link to={Routes.friendlyUserPath(currentUser.account, { anchor: "notes" })}>
                  <FormattedMessage id="header.entries" />
                </Link>
              </li>
            )}
            {currentUser.canPostNote !== false && (
              <li>
                {/* eslint-disable @typescript-eslint/naming-convention */}
                <Link
                  to={Routes.friendlyUserPath(currentUser.account, {
                    notes_tab: "private",
                    anchor: "notes",
                  })}
                >
                  <FormattedMessage id="header.drafts" />
                </Link>
                {/* eslint-enable @typescript-eslint/naming-convention */}
              </li>
            )}
          </ul>
          {currentUser.canCreateLike !== false && (
            <ul>
              <li>
                {/* eslint-disable @typescript-eslint/naming-convention */}
                <Link to={Routes.myLikesPath()}>
                  <FormattedMessage id="header.likedByMe" />
                </Link>
                {/* eslint-enable @typescript-eslint/naming-convention */}
              </li>
            </ul>
          )}
          {currentUser.assistantEnabled && (
            <ul>
              <li>
                <Link to={Routes.assistantsPath()}>
                  <div css={AssistantItemStyle}>
                    <FormattedMessage id="header.assistant" />
                    <div css={AssistantFeatureLabelStyle}>
                      <FeatureLabel>
                        <FormattedMessage id="features.testVersion" />
                      </FeatureLabel>
                    </div>
                  </div>
                </Link>
              </li>
            </ul>
          )}
          <ul>
            <li>
              <Link to={Routes.settingsPath()}>
                <FormattedMessage id="header.settings" />
              </Link>
            </li>
            {currentUser.canManageMembers && (
              <li>
                <Link to={Routes.teamMembersPath()}>
                  <FormattedMessage id="header.manageMembers" />
                </Link>
              </li>
            )}
          </ul>
          <ul>
            <TeamSwitchMenu teams={this.props.teams} intl={this.props.intl} />
            <li>
              <Link to={Routes.findTeamPath()}>
                <FormattedMessage id="externals.login_to_another_team" />
              </Link>
            </li>
          </ul>
          <ul>
            <li>
              <Link to={Routes.destroyUserSessionPath()} onClick={logout} data-method="delete">
                <FormattedMessage id="header.logout" />
              </Link>
            </li>
          </ul>
        </div>

        <ul className="headerNavigation-avatarDropdown__footerItems">
          <li>
            <Link to={Routes.helpsPath()} className="headerNavigation-avatarDropdown__footerLink" target="_blank">
              <FormattedMessage id="header.avatarFooter.help" />
            </Link>
          </li>
          <li>
            <Link to={Routes.kibelablogPath()} className="headerNavigation-avatarDropdown__footerLink" target="_blank">
              <FormattedMessage id="header.avatarFooter.blog" />
            </Link>
          </li>
          <li>
            <Link to={Routes.apiPath()} className="headerNavigation-avatarDropdown__footerLink" target="_blank">
              <FormattedMessage id="header.avatarFooter.api" />
            </Link>
          </li>
          <li>
            <Link to={Routes.statusPath()} className="headerNavigation-avatarDropdown__footerLink" target="_blank">
              <FormattedMessage id="header.avatarFooter.status" />
            </Link>
          </li>
          <li>
            <Link to={Routes.bitjourneyPath()} className="headerNavigation-avatarDropdown__footerLink" target="_blank">
              <FormattedMessage id="header.avatarFooter.company" />
            </Link>
          </li>
        </ul>
      </div>
    );
  }

  render() {
    return (
      <div className="dropdownBox-container smartphoneUserAvatar">
        <button type="button" onClick={this.handleClick} className="button is-button-header">
          <UserAvatar user={this.props.currentUser} isLink={false} size="medium" />
          <OnlySmartphone>
            <div className="smartphoneUserAvatar-label">{this.props.currentUser.account}</div>
          </OnlySmartphone>
        </button>
        {this.state.isMenuOpen && this.dropdownMenu()}
      </div>
    );
  }
}

export const AvatarButton: React.ComponentClass<Props> = onClickOutside(AvatarButtonBase);
